import { IActionFn, IPageData } from '@/store/types';
import { IModuleState } from './types';
import { $http } from '@/axios';
import store from '@/store';
import { sleep } from '@/utils/helper';

// 查询杂志列表
export const queryMagazinePages: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setMagazinePages', { ...state.magazinePage, loading: true });
    const res: IPageData = await $http.post('/v1/design/dpg/pagelist', payload);
    const { list: data, total, pageNum, pageSize } = res;
    commit('setMagazinePages', { data, total, pageNum, pageSize });
  } catch (e) {
    commit('setMagazinePages', { data: [], total: 0, pageNum: 0, pageSize: 0 });
  }
};

// 查询杂志详情
export const queryMagazineDetail: IActionFn<IModuleState> = async ({ commit }, dpgId) => {
  try {
    commit('setMagazineDetail', { data: null, loading: true });
    const res = await $http.get(`/v1/design/dpg/${dpgId}/detail`);
    commit('setMagazineDetail', { data: res });
  } catch (e) {
    commit('setMagazineDetail', { data: null });
  }
};

// 查询平台的专案组列表
export const queryPlatformDpgPages: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setPlatformDpgPages', { ...state.dpgPage, loading: true });
    const params = { ...payload, orgCode: 'platform' };
    const res: IPageData = await $http.post('/v1/design/dpg/pagelist', params);
    const { list: data, pageNum, pageSize, total } = res;
    commit('setPlatformDpgPages', { data, pageNum, pageSize, total });
  } catch (e) {
    commit('setPlatformDpgPages', { data: [], pageNum: 0, pageSize: 0, total: 0 });
  }
};

// 查询专案组详情
// 虽然接口跟杂志详情是一样的，但是请求的响应值存入的 state 不一样，所以最好不要共用
export const queryDpgDetail: IActionFn<IModuleState> = async ({ commit }, dpgId) => {
  try {
    commit('setDpgDetail', { data: null, loading: true });
    const res = await $http.get(`/v1/design/dpg/${dpgId}/detail`);
    commit('setDpgDetail', { data: res });
  } catch (e) {
    commit('setDpgDetail', { data: null });
  }
};

// 查询杂志状态字典
export const queryStatusTypes: IActionFn<IModuleState> = async ({ commit, state }) => {
  try {
    commit('setStatusTypes', { ...state.statusTypes, loading: true });
    const res = await $http.get('/common/chooseoptions/DesignProjectStatus');
    commit('setStatusTypes', { data: res });
  } catch (e) {
    commit('setStatusTypes', { data: [] });
  }
};

// 添加杂志(基于专案组生成杂志)
export const addMagazineByDpg: IActionFn<IModuleState> = async ({ commit }, payload) => {
  try {
    commit('setLoading', true);
    await $http.post('/v1/design/dpgtomagazine', payload);
    store.commit('common/setToast', { visible: true, text: '新增成功' });
  } finally {
    commit('setLoading', false);
  }
};

// 编辑杂志
export const editMagazine: IActionFn<IModuleState> = async ({ commit }, payload) => {
  try {
    const { dpgId, ...rest } = payload;
    commit('setLoading', true);
    await $http.post('/v1/design/mag/modify', rest);
    // 保存后查询详情并更新 state
    const res = await $http.get(`/v1/design/dpg/${dpgId}/detail`);
    commit('setMagazineDetail', { data: res });
    store.commit('common/setToast', { visible: true, text: '保存成功' });
  } finally {
    commit('setLoading', false);
  }
};

// 删除杂志
export const deleteMagazine: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setConfirm', { ...state.confirm, loading: true });
    await $http.post('/v1/design/mag/delete', payload);
    store.commit('common/setToast', { visible: true, text: '删除成功' });
  } finally {
    commit('setConfirm', { visible: false });
  }
};

// 下架杂志
export const shelfMagazine: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setConfirm', { ...state.confirm, loading: true });
    await $http.post('/v1/design/dpg/shelf', payload);
  } finally {
    commit('setConfirm', { visible: false });
  }
};

// 发布杂志
export const publishMagazine: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setConfirm', { ...state.confirm, loading: true });
    const res: any = await $http.post('/v1/design/dpg/shelf', payload, { ignoreCode: [1626] });
    if (res?.rtnCode === 1626) {
      // 如果出现了 1626 业务错误，说明发布的专案组里存在未发布的物料，此时打开强制发布弹窗
      const resData = res?.data || [];
      const data = JSON.parse(resData[0] || '[]');
      commit('setConfirm', { visible: true, type: 'publish-release', data });
      return true;
    } else {
      store.commit('common/setToast', { visible: true, text: '发布成功' });
      commit('setConfirm', { visible: false });
      return false;
    }
  } catch (e) {
    commit('setConfirm', { visible: false });
    return false;
  }
};

// 强制发布杂志(二次确认后，才能强制发布)
export const publishRelease: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setConfirm', { ...state.confirm, loading: true });
    await $http.post('/v1/design/dpg/shelf', payload);
    store.commit('common/setToast', { visible: true, text: '发布成功' });
  } finally {
    commit('setConfirm', { visible: false });
  }
};

// 查询已保存的过滤项配置
export const querySavedFilters: IActionFn<IModuleState> = async ({ commit }, payload) => {
  try {
    commit('setSavedFilters', { data: [], loading: true });
    const res = await $http.post('/v1/design/dpg/filter/list', payload);
    commit('setSavedFilters', { data: res || [], loading: false });
  } catch (e) {
    commit('setSavedFilters', { data: [], loading: false });
  }
};

// 删除过滤项配置
export const deleteMagazineFilter: IActionFn<IModuleState> = async (_, payload) => {
  try {
    await $http.post('/v1/design/dpg/filter/delete', payload);
    return true;
  } catch (e) {
    return false;
  }
};

// 保存过滤项配置
export const saveMagazineFilter: IActionFn<IModuleState> = async (_, payload) => {
  try {
    await $http.post('/v1/design/dpg/filter/modify', payload);
    store.commit('common/setToast', { visible: true, text: '保存成功' });
    return true;
  } catch (e) {
    return false; // 相当于 resolve(false)
  }
};

// 查询待授权的组织列表(包含已授权和未授权)
export const queryAllAuthOrgs: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setAllAuthOrgs', { ...state.allAuthOrgs, loading: true });
    const res: IPageData = await $http.post('/v1/data/baseinfo/brand/authpagelist', payload);
    const { list: data, total, pageNum, pageSize } = res;
    commit('setAllAuthOrgs', { data, total, pageNum, pageSize });
  } catch (e) {
    commit('setAllAuthOrgs', { data: [], total: 0, pageNum: 0, pageSize: 0 });
  }
};

// 查询已授权的组织列表
export const queryAuthedOrgs: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setAuthedOrgs', { ...state.authedOrgs, loading: true });
    const res: IPageData = await $http.post('/v1/data/system/org/listbyauthedmag', payload);
    const { list: data, total, pageNum, pageSize } = res;
    commit('setAuthedOrgs', { data, total, pageNum, pageSize });
  } catch (e) {
    commit('setAuthedOrgs', { data: [], total: 0, pageNum: 0, pageSize: 0 });
  }
};

// 添加授权组织
export const addAuthOrgToMagazine: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setDialog', { ...state.dialog, loading: true });
    await $http.post('/v1/design/mag/auth', payload);
  } finally {
    commit('setDialog', { visible: false });
  }
};

// 取消授权组织
export const deleteAuthOrgFromMagazine: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setConfirm', { ...state.confirm, loading: true });
    await $http.post('/v1/baseinfo/brand/deleteauthorg', payload);
    store.commit('common/setToast', { visible: true, text: '成功取消授权' });
  } finally {
    commit('setConfirm', { visible: false });
  }
};

// 查询待添加的物品列表(左库)
export const queryAllModels: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setAllModels', { ...state.allModels, loading: true });
    const res: IPageData = await $http.post('/v1/data/asset/item/search', payload);
    const { list: data, total, pageNum, pageSize } = res;
    commit('setAllModels', { data, total, pageNum, pageSize });
  } catch (e) {
    commit('setAllModels', { data: [], total: 0, pageNum: 0, pageSize: 0 });
  }
};

// 查询待添加的材料列表(左库)
export const queryAllMaterials: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setAllMaterials', { ...state.allMaterials, loading: true });
    const res: IPageData = await $http.post('/v1/data/asset/material/search', payload);
    const { list: data, total, pageNum, pageSize } = res;
    commit('setAllMaterials', { data, total, pageNum, pageSize });
  } catch (e) {
    commit('setAllMaterials', { data: [], total: 0, pageNum: 0, pageSize: 0 });
  }
};

// 查询杂志的物料库(右库)
export const queryMagazineLibs: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setLibModels', { ...state.libModels, loading: true });
    commit('setLibMaterials', { ...state.libMaterails, loading: true });
    const res: any = await $http.post('/v1/design/magazine/listforlib', payload);
    // const res: any = await $http.post('/v1/design/dpg/listforlib', payload);
    commit('setLibModels', { data: res?.designProjectModelItemDtos || [] });
    commit('setLibMaterials', { data: res?.designProjectMaterialInstanceItemDtos || [] });
  } catch (e) {
    commit('setLibModels', { data: [] });
    commit('setLibMaterials', { data: [] });
  }
};

// 查询物料库的调整日志(右库)
export const queryMagazineLibLogs: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setLibLogs', { ...state.libLogs, loading: true });
    const res: IPageData = await $http.post('/v1/design/dpg/logforitem', payload);
    const { list: data, total, pageNum, pageSize } = res;
    commit('setLibLogs', { data, total, pageNum, pageSize });
  } catch (e) {
    commit('setLibLogs', { data: [], total: 0, pageNum: 0, pageSize: 0 });
  }
};

// 添加物品到物料库(右库)
export const addMagazineModel: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setDialog', { ...state.dialog, loading: true });
    await $http.post('/v1/client/design/magazine/additems', payload);
    // await $http.post('/v1/client/design/dpg/additems', payload);
  } finally {
    commit('setDialog', { visible: false });
  }
};

// 添加材料到物料库(右库)
export const addMagazineMaterial: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setDialog', { ...state.dialog, loading: true });
    await $http.post('/v1/client/design/magazine/addmaterials', payload);
    // await $http.post('/v1/client/design/dpg/addmaterials', payload);
  } finally {
    commit('setDialog', { visible: false });
  }
};

// 删除物料库里的物品(右库)
export const deleteMagazineModel: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setConfirm', { ...state.confirm, loading: true });
    await $http.post('/v1/client/design/magazine/removeitems', payload);
    // await $http.post('/v1/client/design/dgp/removeitems', payload);
  } finally {
    commit('setConfirm', { visible: false });
  }
};

// 删除物料库里的材料(右库)
export const deleteMagazineMaterial: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setConfirm', { ...state.confirm, loading: true });
    await $http.post('/v1/client/design/magazine/removematerials', payload);
    // await $http.post('/v1/client/design/dpg/removematerials', payload);
  } finally {
    commit('setConfirm', { visible: false });
  }
};
